import { number, string } from '@printu/lang-utils';
import getNextUID from 'app/core/markers/getNextUID';
import { getTypeFactory } from 'app/core/markers/getTypeFactory';
import { DefaultTextValues, getCloudPath, getInternalCloudRect, } from 'app/utils/text';
export const KnownFontFamilies = ['Helvetica', 'drawn', 'code'];
export const DefaultFontFamily = KnownFontFamilies[0];
// base font 16px
const emsBaseSize = 16;
const FontSizesInEMS = {
    [3 /* MarkerSize.s1 */]: 1.0,
    [6 /* MarkerSize.s2 */]: 1.4,
    [9 /* MarkerSize.s3 */]: 1.8,
    [12 /* MarkerSize.s4 */]: 2.2,
    [15 /* MarkerSize.s5 */]: 2.6,
    /* old
    [MarkerSize.s1]: 1.8,
    [MarkerSize.s2]: 2.0,
    [MarkerSize.s3]: 2.2,
    [MarkerSize.s4]: 2.3,
    [MarkerSize.s5]: 2.6,
    */
};
const FontSizesInPT = {
    [3 /* MarkerSize.s1 */]: Math.trunc(Math.round(FontSizesInEMS[3 /* MarkerSize.s1 */] * emsBaseSize)),
    [6 /* MarkerSize.s2 */]: Math.trunc(Math.round(FontSizesInEMS[6 /* MarkerSize.s2 */] * emsBaseSize)),
    [9 /* MarkerSize.s3 */]: Math.trunc(Math.round(FontSizesInEMS[9 /* MarkerSize.s3 */] * emsBaseSize)),
    [12 /* MarkerSize.s4 */]: Math.trunc(Math.round(FontSizesInEMS[12 /* MarkerSize.s4 */] * emsBaseSize)),
    [15 /* MarkerSize.s5 */]: Math.trunc(Math.round(FontSizesInEMS[15 /* MarkerSize.s5 */] * emsBaseSize)),
};
export const MarkerSizeToFontSize = {
    rem: FontSizesInEMS,
    em: FontSizesInEMS,
    pt: FontSizesInPT,
};
export const DefaultFontSizeMetrics = 'pt';
export const DefaultTextFormat = {
    fontFamily: DefaultFontFamily,
    fontSize: MarkerSizeToFontSize[DefaultFontSizeMetrics][3 /* MarkerSize.s1 */],
    lineHeight: '1.6',
    metrics: DefaultFontSizeMetrics,
};
const AbstractMarkerDefaults = {
    x: 0,
    y: 0,
};
const ConvexMarkerDefaults = {
    endX: 0,
    endY: 0,
};
export const createPathMarker = getTypeFactory('Path', Object.assign(Object.assign({}, AbstractMarkerDefaults), { data: '' }));
export const createHighlightMarker = getTypeFactory('Highlight', Object.assign(Object.assign({}, AbstractMarkerDefaults), { data: '' }));
export const createPolylineMarker = getTypeFactory('Polyline', Object.assign(Object.assign({}, AbstractMarkerDefaults), { data: '' }));
export const createArrowMarker = getTypeFactory('Arrow', Object.assign(Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults), { type: 'fancy' }));
export const createLineMarker = getTypeFactory('Line', Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults));
export const createEllipseMarker = getTypeFactory('Ellipse', Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults));
export const createRectMarker = getTypeFactory('Rect', Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults));
export const createSpotMarker = getTypeFactory('Spot', Object.assign(Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults), { type: 'ellipse' }));
export const createBlurMarker = getTypeFactory('Blur', Object.assign(Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults), { method: 'pixelate', blur: 10 }));
export const createZoomMarker = getTypeFactory('Zoom', Object.assign(Object.assign(Object.assign({}, AbstractMarkerDefaults), ConvexMarkerDefaults), { type: 'ellipse', width: 80, height: 80, zoom: 1.5 }));
// -----------------------
// TextFormat
// -----------------------
const createTextFormat = (format) => (Object.assign(Object.assign({}, DefaultTextFormat), format));
export function getTextFormat(param0, param1 = undefined) {
    let fontFamily;
    let fontSize;
    if (typeof param0 === 'number') {
        fontSize = number(param0, undefined);
        fontFamily = string(param1, undefined);
    }
    else if (param0 && typeof param0 === 'object') {
        if ('format' in param0) {
            const { format } = param0;
            if (format) {
                return createTextFormat(format);
            }
        }
    }
    const format = {};
    if (fontSize) {
        format.fontSize = fontSize;
        format.metrics = DefaultFontSizeMetrics;
    }
    if (fontFamily) {
        format.fontFamily = fontFamily;
    }
    return createTextFormat(format);
}
export const getMarkerText = (marker) => {
    switch (marker.alias) {
        case 'Text':
            return getTextValue(marker);
        case 'Pin':
            return String(marker.number);
        default:
            break;
    }
    return null;
};
export const getScaledFontSize = (size, sizeFactor = 1.0) => {
    const fontSizeFlt = MarkerSizeToFontSize[DefaultFontSizeMetrics][size] / sizeFactor;
    const fontSize = Math.trunc(Math.round(fontSizeFlt * 100)) / 100;
    return fontSize;
};
// -----------------------
// Text
// -----------------------
export function getTextValue(text) {
    return text.lines.map(line => line.text).join('\n');
}
export function getTextDOMValue(text) {
    return text.lines.map(line => `<p>${line.text}</p>`).join('');
}
export function getTextValues(marker) {
    if (!marker) {
        return { plain: null, html: null };
    }
    return {
        plain: getTextValue(marker),
        html: getTextDOMValue(marker),
    };
}
export function hasDefaultText(text) {
    return (text.lines.length === 1 &&
        text.lines[0].text === DefaultTextValues.singleline);
}
export const createTextMarker = getTypeFactory('Text', Object.assign(Object.assign({}, AbstractMarkerDefaults), { format: DefaultTextFormat, lines: [], width: 0, height: 0 }));
export const createPinMarker = getTypeFactory('Pin', Object.assign(Object.assign({}, AbstractMarkerDefaults), { format: DefaultTextFormat, number: 1, width: 0, height: 0, innerWidth: 0, innerHeight: 0, descent: 0 }));
export const createImageMarker = getTypeFactory('Image', Object.assign(Object.assign({}, AbstractMarkerDefaults), { crop: undefined }));
export const MarkerTypeInheritance = {
    Arrow: ['AbstractMarker', 'TwoPointsMarker', 'Arrow'],
    Line: ['AbstractMarker', 'TwoPointsMarker', 'Line'],
    Ellipse: ['AbstractMarker', 'TwoPointsMarker', 'Ellipse'],
    Rect: ['AbstractMarker', 'TwoPointsMarker', 'Rect'],
    Spot: ['AbstractMarker', 'TwoPointsMarker', 'Spot'],
    Blur: ['AbstractMarker', 'TwoPointsMarker', 'Blur'],
    Zoom: ['AbstractMarker', 'TwoPointsMarker', 'Zoom'],
    Polyline: ['AbstractMarker', 'Polyline'],
    Path: ['AbstractMarker', 'Path'],
    Highlight: ['AbstractMarker', 'Highlight'],
    Text: ['AbstractMarker', 'MarkerWithFormat', 'Text'],
    Label: ['AbstractMarker', 'MarkerWithFormat', 'Text', 'Label'],
    Pin: ['AbstractMarker', 'MarkerWithFormat', 'Pin'],
    Image: ['AbstractMarker', 'Image'],
};
// -----------------------
// Marker layer
// -----------------------
const MarkerLayers = [
    ['Blur'],
    ['Spot'],
    [
        'Arrow',
        'Ellipse',
        'Rect',
        'Line',
        'Polyline',
        'Path',
        'Highlight',
        'Zoom',
        'Image',
    ],
    ['Pin'],
    ['Text', 'Label'],
];
const MarkerAliasToLayer = MarkerLayers.reduce((map, set, setIndex) => {
    set.forEach(alias => {
        map[alias] = setIndex;
    });
    return map;
}, {});
export const getMarkerLayer = (alias) => MarkerAliasToLayer[alias];
export const getMarkersInLayer = (layer) => MarkerLayers[layer].length;
// -----------------------
// Style
// -----------------------
export const getMarkerStyles = (marker) => ({
    strokeColor: marker.color,
    strokeWidth: marker.size,
});
export const getTextMarkerPatch = (marker, patch) => {
    var _a, _b, _c;
    const finalPatch = Object.assign({}, patch);
    const backgroundType = (_a = marker.background) === null || _a === void 0 ? void 0 : _a.type;
    switch (backgroundType) {
        case 'cloud':
            {
                const { radius, tail } = marker.background;
                const pathData = getCloudPath(Object.assign(Object.assign({}, getInternalCloudRect((_b = finalPatch.width) !== null && _b !== void 0 ? _b : marker.width, (_c = finalPatch.height) !== null && _c !== void 0 ? _c : marker.height)), { radius,
                    tail }));
                finalPatch.background = Object.assign(Object.assign({}, marker.background), { data: pathData });
            }
            break;
        default:
            break;
    }
    return finalPatch;
};
// -----------------------
// Utils
// -----------------------
export function cloneItem(item) {
    return item
        ? Object.assign(Object.assign({}, item), { id: getNextUID() }) : undefined;
}
export function is(object, alias) {
    return object && object.alias === alias;
}
